// src/graph-driver.js
//
// The contract every backend driver implements. JS has no real interfaces,
// so this is a base class whose methods throw — a driver extends it and
// overrides what it needs; calling something a driver never implemented is
// a bug in that driver, not a silent no-op that draws nothing.
//
// Deliberately thin: mount/setData/on/fit/resize/destroy is the whole
// contract, because that is everything GraphView needs to stay ignorant of
// which of the three backends it is actually driving.

export class GraphDriver {
    /**
     * @param {HTMLElement} container
     * @param {object} [options]
     */
    mount(container, options = {}) {
        throw new Error('GraphDriver.mount() not implemented');
    }

    /**
     * Replaces whatever data is currently shown — never a diff/patch. A
     * driver that wants incremental updates can still implement this by
     * diffing internally; nothing above this layer needs to know either way.
     *
     * @param {Array<{id: string, label?: string, group?: string, [key: string]: any}>} nodes
     * @param {Array<{id?: string, source: string, target: string, label?: string, [key: string]: any}>} edges
     */
    setData(nodes, edges) {
        throw new Error('GraphDriver.setData() not implemented');
    }

    /**
     * @param {'click'|'hover'|'select'|'drag'} event  'drag' fires continuously
     *        while a node is being dragged, not just once at the end
     * @param {(payload: {type: 'node'|'edge', id: string, data: object, position?: {x: number, y: number}, delta?: {dx: number, dy: number}}) => void} callback
     *        `position`/`delta` are only present on 'drag'
     */
    on(event, callback) {
        throw new Error('GraphDriver.on() not implemented');
    }

    /**
     * Shift one node by a relative offset — never an absolute position, so
     * a caller never needs to read the current one first just to move it.
     * The counterpart to 'drag': the usual reason to call this is a parent
     * node's own 'drag' handler moving its descendants by the same delta.
     *
     * @param {string} id
     * @param {number} dx
     * @param {number} dy
     */
    moveNode(id, dx, dy) {
        throw new Error('GraphDriver.moveNode() not implemented');
    }

    /** Frame everything currently in the graph into view. */
    fit() {
        throw new Error('GraphDriver.fit() not implemented');
    }

    /** The container's own size changed — re-measure and re-fit. */
    resize() {
        throw new Error('GraphDriver.resize() not implemented');
    }

    /** Release whatever the backend allocated. After this, the driver is not reusable. */
    destroy() {
        throw new Error('GraphDriver.destroy() not implemented');
    }
}
